// components/website/track-pageview.tsx
"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { readConsentFromDocument } from "@/lib/consent";
import { recordPageview } from "@/actions/analytics";

const ANON_COOKIE = "fm-aid";
const ANON_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 12 months

function readOrCreateAnonId(): string {
  if (typeof document === "undefined") return "";
  const existing = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${ANON_COOKIE}=`))
    ?.split("=")[1];
  if (existing) return existing;
  // Use crypto.randomUUID if available (all modern browsers do, but
  // be defensive in case of weird embedded webview).
  const id = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : "00000000-0000-4000-8000-" + Math.random().toString(16).slice(2, 14).padStart(12, "0");
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${ANON_COOKIE}=${id}; Path=/; Max-Age=${ANON_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  return id;
}

/**
 * Records a page view to `analytics_pageviews` when the visitor has
 * consent="all". Renders nothing. Mounted in the public-site layouts
 * so every route change fires one insert.
 *
 * Re-fires whenever the pathname changes (Next.js App Router gives us
 * the new path through usePathname). Search-param changes do NOT
 * trigger a new view — they'd cause too many inserts during e.g. a
 * search-as-you-type page.
 */
export function TrackPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (readConsentFromDocument() !== "all") return;
    const anonId = readOrCreateAnonId();
    if (!anonId) return;

    // UTM capture — only on the first request of a session (the server
    // dedupes by checking for an existing session within 30 min).
    const utm_source   = searchParams.get("utm_source");
    const utm_medium   = searchParams.get("utm_medium");
    const utm_campaign = searchParams.get("utm_campaign");
    const utm_term     = searchParams.get("utm_term");
    const utm_content  = searchParams.get("utm_content");

    const locale = (() => {
      const first = pathname.split("/")[1];
      return first === "en" || first === "ar" ? first : null;
    })();

    // Fire-and-forget — never block render on telemetry.
    void recordPageview({
      anon_id: anonId,
      path: pathname,
      locale,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      utm_source, utm_medium, utm_campaign, utm_term, utm_content,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
}
